import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";

export const PlanFeatures = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: plans, isLoading } = useQuery({
    queryKey: ["plans"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("plans")
        .select("*")
        .order("max_workspaces");
      
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!plans || plans.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">{t('plans.noPlansAvailable')}</p>
      </div>
    );
  }

  const handleSelectPlan = (planId: string) => {
    navigate(`/auth?mode=signup&plan=${planId}`);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 w-full">
      {plans.map((plan) => (
        <Card key={plan.id} className="flex flex-col w-full">
          <CardHeader>
            <CardTitle className="text-2xl font-bold">{plan.name}</CardTitle>
          </CardHeader>
          <CardContent className="flex-grow flex flex-col">
            <div className="space-y-4 flex-grow">
              <FeatureItem text={t('plans.maxWorkspaces', { count: plan.max_workspaces })} />
              <FeatureItem text={t('plans.maxBoardsPerWorkspace', { count: plan.max_boards_per_workspace })} />
              <FeatureItem text={t('plans.maxPomodorosPerWorkspace', { count: plan.max_pomodoros_per_workspace })} />
              <FeatureItem text={t('plans.canAddCollaborators')} checked={plan.can_add_collaborators} />
              <FeatureItem text={t('plans.canShareWithExternalUsers')} checked={plan.can_share_with_external_users} />
            </div>
            <Button 
              className="w-full mt-6" 
              onClick={() => handleSelectPlan(plan.id)}
              variant={plan.name === "Pro" ? "default" : "outline"}
            >
              {t('plans.selectPlan')}
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const FeatureItem = ({ text, checked = true }: { text: string; checked?: boolean }) => (
  <div className="flex items-center gap-2">
    <Check className={`h-5 w-5 ${checked ? 'text-primary' : 'text-gray-300'}`} />
    <span className={checked ? '' : 'text-gray-400'}>{text}</span>
  </div>
);